// Admin Undo mutation hooks.
//
// One useMutation per reversible action. Each hook invalidates the
// caches that the corresponding forward action would have populated so
// the UI reverts in-place without a manual refresh. Errors propagate
// to the caller's onError; the toast wrapper in the calling component
// surfaces the friendly precondition message from the server (422
// AdminUndoPreconditionException).
//
// These live in their own hook module so further undo surfaces can
// extend it without thrashing the general-purpose useDealFlow.ts file.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminUndoApi } from "@/api/dealFlow";
import type {
  AdminUndoLogDto,
  AdminUndoResultDto,
  CriteriaLane,
} from "@/api/dealFlow/types";

function invalidateUndoCaches(
  qc: ReturnType<typeof useQueryClient>,
  dealId: string,
) {
  // Deal-flow tab payload — every undo can affect at least one panel.
  qc.invalidateQueries({ queryKey: ["deal-flow", "assignments", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "checklist", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "lane-assessments", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "outcome", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "certifications", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "escrow", dealId] });
  qc.invalidateQueries({ queryKey: ["deals", "detail", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "admin-undo-log", dealId] });
}

export function useUndoSection9Handoff(dealId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.section9Handoff(dealId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useUndoS8CertSign(dealId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, { certificationCode: string; reason: string }>({
    mutationFn: ({ certificationCode, reason }) =>
      adminUndoApi.s8CertSign(dealId, certificationCode, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useUndoDealOutcomeFinalize(dealId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.dealOutcomeFinalize(dealId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useUndoLaneAssessment(dealId: string, lane: CriteriaLane) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.laneAssessment(dealId, lane, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useUndoCriteriaItemTerminal(dealId: string, checklistItemId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.criteriaItemTerminal(checklistItemId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

/** Owner ruling 15, 2026-10-08: a Certified analyst-tab Section 2
 *  certification changes only through this undo. */
export function useUndoAnalystS2Certification(dealId: string, certificationId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.analystS2Certification(certificationId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

/** Owner approval, 2026-10-13: a Certified Closing Step 1 certification changes
 *  only through this undo, refused once the loan is created. */
export function useUndoClosingS1Certification(dealId: string, certificationId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.closingS1Certification(certificationId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useUndoEscrowIssuance(dealId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.escrowIssuance(dealId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useUndoEscrowFundingVerification(dealId: string) {
  const qc = useQueryClient();
  return useMutation<AdminUndoResultDto, Error, string>({
    mutationFn: (reason: string) =>
      adminUndoApi.escrowFunding(dealId, { reason }),
    onSuccess: () => invalidateUndoCaches(qc, dealId),
  });
}

export function useAdminUndoLog(dealId: string | undefined) {
  return useQuery<AdminUndoLogDto[]>({
    queryKey: ["deal-flow", "admin-undo-log", dealId ?? ""],
    queryFn: () => adminUndoApi.listForDeal(dealId!),
    enabled: !!dealId,
  });
}
